/**
 * Form Filling Page - Enhanced Design
 * 
 * Beautiful conversational form filling with:
 * - Progress visualization
 * - Auto-focus input
 * - Sidebar with form info
 * - Modern chat design
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import axios from 'axios';
import { FaUser, FaSyncAlt, FaFilePdf } from 'react-icons/fa';
import LogoLoader from '../LogoLoader';
import { BsChatDots } from 'react-icons/bs';
import { IoDocumentText, IoSend, IoCheckmarkCircle, IoTimeOutline, IoCloseCircle } from 'react-icons/io5';
import { HiOutlineLightBulb } from 'react-icons/hi';

// Remove trailing slash from API_BASE to avoid double slashes in URLs
const API_BASE = (process.env.REACT_APP_SERVER || 'http://localhost:3030').replace(/\/$/, '');

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
`;

const progressGlow = keyframes`
  0%, 100% { box-shadow: 0 0 5px rgba(18, 165, 113, 0.3); }
  50% { box-shadow: 0 0 20px rgba(18, 165, 113, 0.6); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const PageWrapper = styled.div`
  height: 100vh;
  min-height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #f9fbff 0%, #ffffff 50%, #f9fbff 100%);
  display: flex;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, Arial, sans-serif;
`;

// Sidebar
const Sidebar = styled.div`
  width: 320px;
  height: 100%;
  overflow-y: auto;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  border-right: 1px solid rgba(91, 97, 234, 0.15);
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  
  &::-webkit-scrollbar {
    width: 0;
  }
  
  @media (max-width: 900px) {
    display: none;
  }
`;

const FormLogo = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #5b61ea 0%, #885de8 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 8px 32px rgba(91, 97, 234, 0.3);
  color: white;
  font-size: 24px;
`;

const FormName = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #192947;
  margin: 0 0 6px 0;
`;

const FormSubtitle = styled.p`
  font-size: 13px;
  color: #455776;
  margin: 0 0 32px 0;
`;

// Progress Circle
const ProgressSection = styled.div`
  margin-bottom: 32px;
`;

const ProgressLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #65748d;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
`;

const CircularProgress = styled.div`
  position: relative;
  width: 140px;
  height: 140px;
  margin: 0 auto 16px;
`;

const CircleSVG = styled.svg`
  transform: rotate(-90deg);
  width: 140px;
  height: 140px;
`;

const CircleBackground = styled.circle`
  fill: none;
  stroke: rgba(91, 97, 234, 0.15);
  stroke-width: 10;
`;

const CircleProgress = styled.circle`
  fill: none;
  stroke: url(#progressGradient);
  stroke-width: 10;
  stroke-linecap: round;
  stroke-dasharray: ${props => props.$circumference};
  stroke-dashoffset: ${props => props.$offset};
  transition: stroke-dashoffset 0.8s ease;
  filter: drop-shadow(0 0 6px rgba(18, 165, 113, 0.4));
`;

const ProgressCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;

const ProgressPercent = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #192947;
  line-height: 1;
`;

const ProgressSubtext = styled.div`
  font-size: 11px;
  color: #65748d;
  margin-top: 4px;
`;

// Stats
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: #f5f8fd;
  border: 1px solid rgba(91, 97, 234, 0.1);
  border-radius: 12px;
  padding: 14px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${props => props.$color || '#192947'};
  margin-bottom: 2px;
`;

const StatLabel = styled.div`
  font-size: 10px;
  color: #65748d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Refresh Button
const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  margin-top: 16px;
  background: linear-gradient(135deg, rgba(91, 97, 234, 0.15) 0%, rgba(136, 93, 232, 0.15) 100%);
  border: 1px solid rgba(91, 97, 234, 0.3);
  border-radius: 10px;
  color: #5874be;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(91, 97, 234, 0.25) 0%, rgba(136, 93, 232, 0.25) 100%);
    border-color: rgba(91, 97, 234, 0.5);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 14px;
  }
`;

// Tips Section
const TipsSection = styled.div`
  margin-top: auto;
  padding-top: 24px;
  border-top: 1px solid rgba(91, 97, 234, 0.1);
`;

const TipCard = styled.div`
  background: linear-gradient(135deg, rgba(91, 97, 234, 0.1) 0%, rgba(136, 93, 232, 0.1) 100%);
  border: 1px solid rgba(91, 97, 234, 0.2);
  border-radius: 12px;
  padding: 16px;
`;

const TipIcon = styled.span`
  font-size: 16px;
  margin-right: 8px;
  color: #a2611c;
  display: flex;
  align-items: center;
`;

const TipText = styled.p`
  font-size: 12px;
  color: rgba(121, 85, 205, 0.9);
  margin: 0;
  line-height: 1.5;
`;

// Main Chat Area
const MainArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
  height: 100%;
  overflow: hidden;
  
  @media (max-width: 900px) {
    padding: 16px 12px;
  }
`;

// Mobile Header (shown on small screens)
const MobileHeader = styled.div`
  display: none;
  padding: 16px 0;
  border-bottom: 1px solid rgba(91, 97, 234, 0.15);
  flex-shrink: 0;
  
  @media (max-width: 900px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;

const MobileFormInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MobileLogo = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #5b61ea 0%, #885de8 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
`;

const MobileTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #192947;
`;

const MobileProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MiniProgressBar = styled.div`
  width: 60px;
  height: 6px;
  background: rgba(91, 97, 234, 0.2);
  border-radius: 3px;
  overflow: hidden;
`;

const MiniProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #12a571 0%, #12a571 100%);
  width: ${props => props.$percent || 0}%;
  transition: width 0.5s ease;
`;

const MobileProgressText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #0f8f63;
`;

// Messages - THE ONLY SCROLLABLE AREA
const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 4px 24px 0;
  margin-right: -4px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(91, 97, 234, 0.05);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(91, 97, 234, 0.25);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(91, 97, 234, 0.4);
  }
`;

const MessageGroup = styled.div`
  margin-bottom: 24px;
  animation: ${fadeIn} 0.4s ease;
`;

const Message = styled.div`
  display: flex;
  gap: 12px;
  flex-direction: ${props => props.$isUser ? 'row-reverse' : 'row'};
  margin-bottom: 8px;
`;

const Avatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  color: #192947;
  
  ${props => props.$isUser ? css`
    background: linear-gradient(135deg, #335cf4 0%, #2d5bed 100%);
    box-shadow: 0 4px 12px rgba(51, 92, 244, 0.3);
  ` : css`
    background: linear-gradient(135deg, #12a571 0%, #0f9a68 100%);
    box-shadow: 0 4px 12px rgba(18, 165, 113, 0.3);
  `}
`;

const MessageContent = styled.div`
  max-width: 75%;
`;

const MessageBubble = styled.div`
  padding: 14px 18px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.6;
  
  ${props => props.$isUser ? css`
    background: linear-gradient(135deg, #335cf4 0%, #2d5bed 100%);
    color: white;
    border-bottom-right-radius: 6px;
    box-shadow: 0 4px 12px rgba(51, 92, 244, 0.25);
  ` : css`
    background: rgba(255, 255, 255, 0.9);
    color: #33445e;
    border: 1px solid rgba(91, 97, 234, 0.15);
    border-bottom-left-radius: 6px;
    backdrop-filter: blur(10px);
  `}
`;

const MessageTime = styled.div`
  font-size: 10px;
  color: #65748d;
  margin-top: 4px;
  text-align: ${props => props.$isUser ? 'right' : 'left'};
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 5px;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(91, 97, 234, 0.15);
  border-radius: 18px;
  border-bottom-left-radius: 6px;
  width: fit-content;
`;


// Saved indicator
const SavedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 12px;
  background: rgba(18, 165, 113, 0.1);
  border: 1px solid rgba(18, 165, 113, 0.2);
  border-radius: 20px;
  font-size: 11px;
  color: #0f8f63;
  animation: ${fadeIn} 0.3s ease;
`;

// Input Area - FIXED AT BOTTOM
const InputArea = styled.div`
  padding: 16px 0 0 0;
  border-top: 1px solid rgba(91, 97, 234, 0.15);
  flex-shrink: 0;
  background: transparent;
`;

const InputContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
  
  @media (max-width: 900px) {
    gap: 8px;
  }
`;

const InputWrapper = styled.div`
  flex: 1;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid rgba(91, 97, 234, 0.25);
  border-radius: 20px;
  padding: 6px 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  min-width: 0;
  
  &:focus-within {
    border-color: #5b61ea;
    box-shadow: 0 0 0 4px rgba(91, 97, 234, 0.15), 0 4px 20px rgba(91, 97, 234, 0.2);
  }
  
  @media (max-width: 900px) {
    padding: 4px 6px;
    border-radius: 16px;
  }
`;

const Input = styled.textarea`
  flex: 1;
  background: transparent;
  border: none;
  color: #192947;
  font-size: 15px;
  padding: 14px 16px;
  outline: none;
  resize: none;
  min-height: 24px;
  max-height: 120px;
  font-family: inherit;
  line-height: 1.5;
  min-width: 0;
  
  &::placeholder {
    color: #65748d;
  }
  
  @media (max-width: 900px) {
    padding: 10px 12px;
    font-size: 14px;
  }
`;

const SendButton = styled.button`
  width: 56px;
  height: 56px;
  min-width: 56px;
  background: linear-gradient(135deg, #5b61ea 0%, #885de8 100%);
  border: none;
  border-radius: 16px;
  color: white;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(91, 97, 234, 0.4);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 24px rgba(91, 97, 234, 0.5);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 900px) {
    width: 44px;
    height: 44px;
    min-width: 44px;
    border-radius: 12px;
    font-size: 16px;
  }
`;

// Status Screens
const StatusScreen = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
  height: 100%;
`;

const StatusIcon = styled.div`
  font-size: 56px;
  margin-bottom: 24px;
  color: ${props => props.$color || '#5b61ea'};
  ${props => props.$animate && css`
    animation: ${pulse} 2s ease-in-out infinite;
  `}
`;

const StatusTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #192947;
  margin: 0 0 12px 0;
`;

const StatusText = styled.p`
  font-size: 15px;
  color: #65748d;
  margin: 0;
  max-width: 400px;
  line-height: 1.6;
`;

// Completed Banner
const CompletedBanner = styled.div`
  background: linear-gradient(135deg, rgba(18, 165, 113, 0.15) 0%, rgba(15, 154, 104, 0.1) 100%);
  border: 1px solid rgba(18, 165, 113, 0.25);
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  animation: ${fadeIn} 0.5s ease;
`;

const CompletedIcon = styled.div`
  font-size: 28px;
  color: #0f8f63;
  display: flex;
  align-items: center;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const CompletedText = styled.div`
  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #0f8f63;
    margin: 0 0 4px 0;
  }
  p {
    font-size: 13px;
    color: rgba(15, 143, 99, 0.7);
    margin: 0;
  }
`;

// ============================================
// COMPONENT
// ============================================

const FormFillingPage = () => {
  const { sessionId } = useParams();
  
  const [status, setStatus] = useState('loading');
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [progress, setProgress] = useState({ percent: 0, collected: 0, total: 0, remaining: 0 });
  const [error, setError] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Progress circle calculations
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress.percent / 100) * circumference;
  
  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Auto-focus input after sending
  useEffect(() => {
    if (!sending && status === 'active') {
      inputRef.current?.focus();
    }
  }, [sending, status, messages]);
  
  // Load session
  useEffect(() => {
    const loadSession = async () => {
      try {
        console.log('🔍 Loading session:', sessionId);
        
        const infoResponse = await axios.get(`${API_BASE}/form-filling/sessions/${sessionId}`);
        
        if (!infoResponse.data.success) {
          setError(infoResponse.data.error);
          setStatus('error');
          return;
        }
        
        const sessionInfo = infoResponse.data.session;
        setSession(sessionInfo);
        setProgress({
          percent: sessionInfo.completionPercent || 0,
          collected: (sessionInfo.prefilledCount || 0) + (sessionInfo.collectedCount || 0),
          total: sessionInfo.totalFields || 0,
          remaining: sessionInfo.remainingCount || sessionInfo.totalFields || 0
        });
        
        if (sessionInfo.status === 'completed') {
          setStatus('completed');
          return;
        }
        
        // Start or RESUME session
        const startResponse = await axios.get(`${API_BASE}/form-filling/sessions/${sessionId}/start`);
        
        if (startResponse.data.success) {
          setSession(prev => ({ ...prev, ...startResponse.data.session }));
          
          // Check if we're resuming an existing session
          if (startResponse.data.isResuming && startResponse.data.conversationHistory?.length > 0) {
            // RESUME: Load existing conversation history
            console.log('📝 Resuming session with', startResponse.data.conversationHistory.length, 'messages');
            setMessages(startResponse.data.conversationHistory.map(msg => ({
              role: msg.role,
              content: msg.content,
              time: new Date(msg.time)
            })));
          } else {
            // NEW: Start fresh with initial message
            console.log('🆕 Starting new session');
            setMessages([{
              role: 'assistant',
              content: startResponse.data.initialMessage,
              time: new Date()
            }]);
          }
          
          setStatus('active');
        } else {
          setError(startResponse.data.error);
          setStatus('error');
        }
      } catch (err) {
        console.error('Error loading session:', err);
        if (err.response?.status === 410) {
          setStatus('expired');
        } else {
          setError(err.response?.data?.error || 'Failed to load form');
          setStatus('error');
        }
      }
    };
    
    if (sessionId) {
      loadSession();
    }
  }, [sessionId]);
  
  // Send message
  const sendMessage = useCallback(async () => {
    if (!inputValue.trim() || sending) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    setSending(true);
    
    // Add user message
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: userMessage,
      time: new Date()
    }]);
    
    try {
      // Note: Server now stores conversation history, no need to send it
      const response = await axios.post(`${API_BASE}/form-filling/sessions/${sessionId}/message`, {
        message: userMessage
      });
      
      if (response.data.success) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.data.response,
          extractedAnswers: response.data.extractedAnswers,
          time: new Date()
        }]);
        
        if (response.data.progress) {
          const p = response.data.progress;
          setProgress({
            percent: p.completionPercent || 0,
            collected: (p.prefilledCount || 0) + (p.collectedCount || 0),
            total: p.totalFields || 0,
            remaining: p.remainingCount || 0
          });
          
          if (p.completionPercent >= 100 || p.remainingCount === 0) {
            setStatus('completed');
            await axios.post(`${API_BASE}/form-filling/sessions/${sessionId}/complete`);
          }
        }
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Sorry, I encountered an error. Please try again.',
          time: new Date()
        }]);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, something went wrong. Please try again.',
        time: new Date()
      }]);
    } finally {
      setSending(false);
    }
  }, [inputValue, sending, sessionId]);
  
  // Handle key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  // Refresh profile data - manually trigger AI profile matching
  const handleRefresh = useCallback(async () => {
    if (refreshing || !sessionId) return;
    
    setRefreshing(true);
    try {
      const response = await axios.post(`${API_BASE}/form-filling/sessions/${sessionId}/refresh`);
      
      if (response.data.success) {
        const p = response.data.progress;
        if (p) {
          setProgress({
            percent: p.completionPercent || 0,
            collected: (p.prefilledCount || 0) + (p.collectedCount || 0),
            total: p.totalFields || 0,
            remaining: p.remainingCount || 0
          });
        }
        
        // Show a brief message about what was updated
        if (response.data.newDataFound > 0) {
          console.log(`Refresh: Found ${response.data.newDataFound} matching fields from profile`);
        }
      }
    } catch (err) {
      console.error('Error refreshing profile data:', err);
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, sessionId]);
  
  // Format time
  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // ============================================
  // RENDER
  // ============================================
  
  if (status === 'loading') {
    return (
      <PageWrapper>
        <StatusScreen>
          <LogoLoader size={200} message="Loading your form" />
          <StatusText style={{ marginTop: 12 }}>Please wait while we prepare everything...</StatusText>
        </StatusScreen>
      </PageWrapper>
    );
  }
  
  if (status === 'expired') {
    return (
      <PageWrapper>
        <StatusScreen>
          <StatusIcon $color="#a2611c"><IoTimeOutline /></StatusIcon>
          <StatusTitle>Link Expired</StatusTitle>
          <StatusText>
            This form filling link has expired. Please contact your advisor to get a new link.
          </StatusText>
        </StatusScreen>
      </PageWrapper>
    );
  }
  
  if (status === 'error') {
    return (
      <PageWrapper>
        <StatusScreen>
          <StatusIcon $color="#d63c3c"><IoCloseCircle /></StatusIcon>
          <StatusTitle>Something Went Wrong</StatusTitle>
          <StatusText>{error || 'Unable to load the form. Please try again later.'}</StatusText>
        </StatusScreen>
      </PageWrapper>
    );
  }
  
  return (
    <PageWrapper>
      {/* Sidebar */}
      <Sidebar>
        <FormLogo><IoDocumentText /></FormLogo>
        <FormName>{session?.formName || 'Form'}</FormName>
        <FormSubtitle>Fill out your information below</FormSubtitle>
        
        {/* Progress Circle */}
        <ProgressSection>
          <ProgressLabel>Your Progress</ProgressLabel>
          <CircularProgress>
            <CircleSVG>
              <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#0f8f63" />
                  <stop offset="100%" stopColor="#0f8f63" />
                </linearGradient>
              </defs>
              <CircleBackground cx="70" cy="70" r={radius} />
              <CircleProgress 
                cx="70" 
                cy="70" 
                r={radius}
                $circumference={circumference}
                $offset={offset}
              />
            </CircleSVG>
            <ProgressCenter>
              <ProgressPercent>{Math.round(progress.percent)}%</ProgressPercent>
              <ProgressSubtext>Complete</ProgressSubtext>
            </ProgressCenter>
          </CircularProgress>
        </ProgressSection>
        
        {/* Stats */}
        <StatsGrid>
          <StatCard>
            <StatValue $color="#0f8f63">{progress.collected}</StatValue>
            <StatLabel>Answered</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue $color="#a2611c">{progress.remaining}</StatValue>
            <StatLabel>Remaining</StatLabel>
          </StatCard>
        </StatsGrid>
        
        {/* Update Button */}
        <RefreshButton 
          onClick={handleRefresh} 
          disabled={refreshing || status === 'completed'}
          $refreshing={refreshing}
        >
          {refreshing ? <LogoLoader compact size={14} label="Updating" /> : <FaSyncAlt />}
          {refreshing ? 'Updating...' : 'Update Profile Data'}
        </RefreshButton>
        
        {/* Generate PDF Button */}
        <RefreshButton 
          onClick={() => window.open(`${API_BASE}/form-filling/sessions/${sessionId}/pdf`, '_blank')}
          disabled={status !== 'completed' && progress.collected < 1}
          style={{ marginTop: '8px', background: 'linear-gradient(135deg, rgba(18, 165, 113, 0.15) 0%, rgba(15, 154, 104, 0.15) 100%)', borderColor: 'rgba(18, 165, 113, 0.3)', color: '#0f8f63' }}
        >
          <FaFilePdf />
          Fill & Download PDF
        </RefreshButton>
        
        {/* Tips */}
        <TipsSection>
          <TipCard>
            <TipIcon><HiOutlineLightBulb /></TipIcon>
            <TipText>
              Answer naturally - just type like you're chatting. The AI will extract the information automatically.
            </TipText>
          </TipCard>
        </TipsSection>
      </Sidebar>
      
      {/* Main Chat Area */}
      <MainArea>
        {/* Mobile Header */}
        <MobileHeader>
          <MobileFormInfo>
            <MobileLogo><IoDocumentText /></MobileLogo>
            <MobileTitle>{session?.formName || 'Form'}</MobileTitle>
          </MobileFormInfo>
          <MobileProgress>
            <button 
              onClick={handleRefresh} 
              disabled={refreshing || status === 'completed'}
              style={{
                background: 'transparent',
                border: 'none',
                color: refreshing ? '#5b61ea' : '#7b8aa3',
                cursor: refreshing ? 'wait' : 'pointer',
                padding: '4px',
                marginRight: '4px'
              }}
              title="Update Profile Data"
            >
              {refreshing ? <LogoLoader compact size={14} label="Updating" /> : <FaSyncAlt />}
            </button>
            <button 
              onClick={() => window.open(`${API_BASE}/form-filling/sessions/${sessionId}/pdf`, '_blank')}
              disabled={status !== 'completed' && progress.collected < 1}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#0f8f63',
                cursor: 'pointer',
                padding: '4px',
                marginRight: '8px'
              }}
              title="Download PDF"
            >
              <FaFilePdf />
            </button>
            <MiniProgressBar>
              <MiniProgressFill $percent={progress.percent} />
            </MiniProgressBar>
            <MobileProgressText>{Math.round(progress.percent)}%</MobileProgressText>
          </MobileProgress>
        </MobileHeader>
        
        {/* Completed Banner */}
        {status === 'completed' && (
          <CompletedBanner>
            <CompletedIcon><IoCheckmarkCircle /></CompletedIcon>
            <CompletedText>
              <h3>All Done!</h3>
              <p>Your information has been saved. You can close this page now.</p>
            </CompletedText>
          </CompletedBanner>
        )}
        
        {/* Messages */}
        <MessagesArea>
          {messages.map((msg, idx) => (
            <MessageGroup key={idx}>
              <Message $isUser={msg.role === 'user'}>
                <Avatar $isUser={msg.role === 'user'}>
                  {msg.role === 'user' ? <FaUser /> : <BsChatDots />}
                </Avatar>
                <MessageContent>
                  <MessageBubble $isUser={msg.role === 'user'}>
                    {msg.content}
                  </MessageBubble>
                  <MessageTime $isUser={msg.role === 'user'}>
                    {formatTime(msg.time)}
                  </MessageTime>
                  {msg.extractedAnswers?.length > 0 && (
                    <SavedBadge>
                      ✓ {msg.extractedAnswers.length} field{msg.extractedAnswers.length > 1 ? 's' : ''} saved
                    </SavedBadge>
                  )}
                </MessageContent>
              </Message>
            </MessageGroup>
          ))}
          
          {sending && (
            <MessageGroup>
              <Message>
                <Avatar><BsChatDots /></Avatar>
                <TypingIndicator>
                  <LogoLoader inline size={18} phrases={['Thinking', 'Reading your answer', 'Preparing the next question']} />
                </TypingIndicator>
              </Message>
            </MessageGroup>
          )}
          
          <div ref={messagesEndRef} />
        </MessagesArea>
        
        {/* Input */}
        {status !== 'completed' && (
          <InputArea>
            <InputContainer>
              <InputWrapper>
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your answer..."
                  disabled={sending}
                  rows={1}
                />
              </InputWrapper>
              <SendButton onClick={sendMessage} disabled={sending || !inputValue.trim()} $sending={sending}>
                {sending ? <LogoLoader compact size={20} onDark label="Sending" /> : <IoSend />}
              </SendButton>
            </InputContainer>
          </InputArea>
        )}
      </MainArea>
    </PageWrapper>
  );
};

export default FormFillingPage;
